import { Settings } from 'lucide-react';
import React from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { type TabularFormat } from './TabularTableView';

interface TabularTableToolbarProps {
  formatInfo: { label: string; isAuto: boolean; confidence: number };
  format: TabularFormat;
  editMode: boolean;
  isEmpty: boolean;
  rowCount: number;
  columnCount: number;
  onFormatChange?: (format: string) => void;
  onConvertFormat?: (targetDelimiter: string) => void;
  onAddRow?: () => void;
  onAddCol?: () => void;
}

const FORMAT_BADGE_STYLES: Record<string, string> = {
  CSV: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
  TSV: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/30',
  PIPE: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
  SEMICOLON: 'text-amber-400 bg-amber-400/10 border-amber-400/30',
};

function getFormatBadgeStyle(label: string): string {
  return FORMAT_BADGE_STYLES[label] ?? 'text-muted-foreground bg-white/5 border-border';
}

export const TabularTableToolbar: React.FC<TabularTableToolbarProps> = ({
  formatInfo,
  format,
  editMode,
  isEmpty,
  rowCount,
  columnCount,
  onFormatChange,
  onConvertFormat,
  onAddRow,
  onAddCol,
}) => {
  return (
    <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
      <div className="flex items-center gap-2">
        <TooltipProvider delayDuration={300}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={cn('text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border shrink-0', getFormatBadgeStyle(formatInfo.label))}
              >
                {formatInfo.label}
              </span>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-[11px]">
              {formatInfo.isAuto
                ? `Auto-detected: ${formatInfo.label} (${formatInfo.confidence}% confidence)`
                : `Manual: ${formatInfo.label}`}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <span className="text-xs text-muted-foreground">
          {isEmpty
            ? 'Empty table'
            : `Rows: ${rowCount.toLocaleString()} · Cols: ${columnCount.toLocaleString()}`}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {(onFormatChange || (editMode && onConvertFormat)) && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="p-1 rounded text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
                aria-label="Table settings"
              >
                <Settings className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 p-3">
              {onFormatChange && (
                <div className="mb-3">
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Interpret as</div>
                  <div className="flex flex-wrap gap-1">
                    {(['auto', 'csv', 'tsv', 'pipe', 'semicolon'] as const).map((f) => (
                      <button
                        key={f}
                        type="button"
                        className={cn(
                          'text-[11px] px-2 py-0.5 rounded border transition-colors',
                          format === f
                            ? 'border-violet-400 bg-violet-400/10 text-violet-300 font-medium'
                            : 'border-border text-muted-foreground hover:border-violet-400/50 hover:text-foreground',
                        )}
                        onClick={() => onFormatChange(f)}
                      >
                        {f === 'auto' ? 'Auto' : f.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {editMode && onConvertFormat && !isEmpty && (
                <div>
                  <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-1.5">Convert to</div>
                  <div className="flex flex-wrap gap-1">
                    {([
                      { label: 'CSV', delim: ',' },
                      { label: 'TSV', delim: '\t' },
                      { label: 'Pipe', delim: '|' },
                      { label: 'Semicolon', delim: ';' },
                    ] as const).map((opt) => (
                      <button
                        key={opt.label}
                        type="button"
                        className="text-[11px] px-2 py-0.5 rounded border border-border text-muted-foreground hover:border-violet-400/50 hover:text-foreground transition-colors"
                        onClick={() => onConvertFormat(opt.delim)}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </PopoverContent>
          </Popover>
        )}

        {editMode && onAddRow && (
          <button
            type="button"
            onClick={onAddRow}
            className="text-xs px-2 py-1 rounded border border-dashed border-border text-muted-foreground hover:border-violet-400 hover:text-violet-400 transition-colors"
          >
            ＋ Row
          </button>
        )}
        {editMode && onAddCol && (
          <button
            type="button"
            onClick={onAddCol}
            className="text-xs px-2 py-1 rounded border border-dashed border-border text-muted-foreground hover:border-violet-400 hover:text-violet-400 transition-colors"
          >
            ＋ Column
          </button>
        )}
      </div>
    </div>
  );
};
